import { useState, useEffect, useCallback, useMemo } from 'react';
import { Avatar } from '@picsart/design-system/components/Avatar';
import { AdaptiveMenu } from '@picsart/design-system/components/AdaptiveMenu';
import { BaseBottomSheet } from '@picsart/design-system/components/BaseBottomSheet';
import { PanelHeading } from '@picsart/design-system/components/PanelHeading';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';
import { useTranslator } from '@picsart/rc/services/localization';
import { useDeviceType } from '@picsart/design-system/hooks/device';
import { useAppStore, type IAppStore } from '@/store';
import { getContext } from '@/context';
import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { refreshMonetization } from '@/features/credits';
import { SdkEventType } from '@/utils/user/publishSdkEvent';
import { logout } from '@/utils/user/authActions';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import tierToBadge from '@/utils/user/tierToBadge';
import UserMenuContent from '../UserMenuContent';
import useStyles from './styles';
import type { UserMenuButtonProps } from './types';

const selector = (store: IAppStore) => store.creditsInfo;

const UserMenuButton = ({
  isAuthorized,
  username,
  avatarSrc,
  tierId,
  showUserMenu,
  onCloseApp,
  onSettingsClick,
  onAvatarClick,
}: UserMenuButtonProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { isDesktop } = useDeviceType();
  const [menuOpen, setMenuOpen] = useState(false);
  const creditsInfo = useAppStore(selector);
  const credits = creditsInfo
    ? (creditsInfo.credits ?? 0) + (creditsInfo.addonCredits ?? 0)
    : null;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen || !isAuthorized) return;
    refreshMonetization();
  }, [menuOpen, isAuthorized]);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    await logout();
  }, []);

  const handleOpenCreditsScreen = useCallback(async () => {
    try {
      const result = await getContext().handlers.publishEvent({
        event: SdkEventType.OpenOfferScreen,
        data: {
          source: MINIAPP_PACKAGE_ID,
          action: MINIAPP_PACKAGE_ID,
          touchpoint: MINIAPP_PACKAGE_ID,
          is_pro_touchpoint: true,
          toolId: 'genai-studio-v2',
        },
      });
      if (result) {
        refreshMonetization();
      }
    } catch (error) {
      errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
    }
  }, []);

  const handleOpenRegistration = useCallback(() => {
    setMenuOpen(false);
    openRegistrationModal();
  }, []);

  const handleSettingsClick = useCallback(() => {
    setMenuOpen(false);
    onSettingsClick?.();
  }, [onSettingsClick]);

  const sheetSnapPoints = useMemo(
    () => [isAuthorized ? 320 : 220],
    [isAuthorized]
  );

  if (!showUserMenu) {
    return (
      <InlineButton
        dataTestId="genai-user-menu-close"
        size="large"
        centerIcon={IconCross}
        onClick={onCloseApp}
        aria-label="Close"
        isWithoutBorder
        isWithoutBackground
      />
    );
  }

  const avatarBadge = tierToBadge(tierId);

  const trigger = (
    <button
      type="button"
      data-testid="user-menu-avatar"
      className={classes.avatarBtn}
      onClick={() => {
        // Mobile: open the full profile screen instead of the menu.
        if (!isDesktop && onAvatarClick) {
          onAvatarClick();
          return;
        }
        setMenuOpen((v) => !v);
      }}
      aria-label="User menu"
    >
      <Avatar
        size={32}
        imageSrc={isAuthorized ? avatarSrc : undefined}
        badge={isAuthorized ? avatarBadge : undefined}
      />
    </button>
  );

  const menuContent = (
    <div className={classes.menuPanel}>
      <UserMenuContent
        isAuthorized={isAuthorized}
        username={username}
        avatarSrc={avatarSrc}
        avatarBadge={avatarBadge}
        credits={credits}
        onCreditsClick={handleOpenCreditsScreen}
        onSettingsClick={handleSettingsClick}
        onLogout={handleLogout}
        onSignIn={handleOpenRegistration}
      />
    </div>
  );

  if (!isDesktop) {
    return (
      <div className={classes.wrapper}>
        {trigger}
        <BaseBottomSheet
          isOpen={menuOpen}
          onClose={closeMenu}
          snapPoints={sheetSnapPoints}
          defaultActiveIndex={0}
        >
          <div className={classes.sheetHeading}>
            <PanelHeading
              title={translate('Account')}
              closeAction={closeMenu}
            />
          </div>
          {menuContent}
        </BaseBottomSheet>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <AdaptiveMenu
        dataTestId="user-menu"
        isVisible={menuOpen}
        handleClose={closeMenu}
        title={translate('Account')}
        reference={trigger}
      >
        {menuContent}
      </AdaptiveMenu>
    </div>
  );
};

export default UserMenuButton;
